import { useCallback, useEffect, useMemo, useState } from "react";
import { Appointment, TAptFieldOption, TBusinessUser } from "@elixir/types";
import usePushAptFieldToApt from "../../../../apis/tryst/use-push-apt-field-to-apt";
import SelectionDropdown from "./selection-dropdown";
import { businessUsersFuse } from "../../../../utils/populate-business-users";
import { getAllBusinessUsers } from "apis";
import { getIdb } from "../../../../utils/bridges/methods";
import { debounce } from "@elixir/utils";
import useGetQueueConfig from "../../../../apis/tryst/get-queue-config";

const convertBusinessUserToAptFieldOption = (
  businessUser: TBusinessUser
): TAptFieldOption => ({
  id: businessUser.oid,
  value: businessUser.fln,
});

const PersonSelectDropdown = ({
  apt,
  fieldId,
  isDisabled = false,
}: {
  apt: Appointment;
  fieldId: string;
  isDisabled?: boolean;
}) => {
  const { data: queueConfig } = useGetQueueConfig();
  const { apt_fields } = queueConfig?.data || {};
  const fieldItem = apt_fields?.find((field) => field.field_id === fieldId);
  const { title } = fieldItem || {};
  const { fieldMeta } = fieldItem || {};
  const { subType } = fieldMeta || {};
  const [fieldOptions, setFieldOptions] = useState<TAptFieldOption[]>([]);

  const { mutate: pushAptFieldToApt } = usePushAptFieldToApt();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [filteredOptions, setFilteredOptions] = useState<TAptFieldOption[]>([]);
  const [fetchUsers, setFetchUsers] = useState<boolean>(false);

  useEffect(() => {
    const onMount = async () => {
      const idb = getIdb();
      const localBusinessUsers: TBusinessUser[] = await idb
        ?.table("businessUsers")
        ?.toArray();

      const selectedOptions =
        apt.apt_fields?.find((field) => field.field_id === fieldId)?.values ??
        [];

      setFieldOptions(
        selectedOptions.map((option) => ({
          id: option,
          value:
            localBusinessUsers.find((user) => user.oid === option)?.fln ||
            option,
        }))
      );
    };
    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!fetchUsers) return;

    async function fetchInitialOptions() {
      const idb = getIdb();
      const localBusinessUsers: TBusinessUser[] = await idb
        ?.table("businessUsers")
        .limit(20)
        ?.toArray();

      const selectedOptions =
        apt.apt_fields?.find((field) => field.field_id === fieldId)?.values ??
        [];

      const aptOptionsFromUsers = localBusinessUsers.map(
        convertBusinessUserToAptFieldOption
      );
      const transformedOptions = aptOptionsFromUsers.map((aptOption) => ({
        ...aptOption,
        isSelected:
          selectedOptions.filter(
            (selectedOption) => selectedOption === aptOption.id
          ).length > 0,
      }));

      selectedOptions.forEach((id) => {
        const isPresent = !!transformedOptions.find(
          (aptOption) => aptOption.id === id
        );
        if (!isPresent) {
          transformedOptions.push({
            id,
            value: localBusinessUsers.find((t) => t.oid === id)?.fln || id,
            // description: id,
            isSelected: true,
          });
        }
      });
      setFieldOptions(transformedOptions);
    }

    function pushUsersToFuse(users: TBusinessUser[]) {
      users?.forEach((user) => {
        if (!user.archive) {
          businessUsersFuse.add(user);
        } else {
          businessUsersFuse.remove((fuseUser) => fuseUser.oid === user.oid);
        }
      });
    }
    async function fetchDiffUsers() {
      const idb = getIdb();
      const businessConfig = await idb?.table("misc")?.get("businessConfig");
      const since = businessConfig?.config?.localBusinessUsersUpdatedAt;

      const { data: diffBusinessUsersData } = await getAllBusinessUsers({
        since,
      });

      const { users: diffBusinessUsers = [], time_cursor } =
        diffBusinessUsersData || {};
      if (time_cursor) {
        idb?.businessUsers
          ?.bulkPut?.(diffBusinessUsers.map((item) => ({ ...item })))
          .then(() => {
            idb?.misc?.put?.({
              ...businessConfig,
              key: "businessConfig",
              config: {
                ...businessConfig?.config,
                localBusinessUsersUpdatedAt: time_cursor,
              },
            });
          });
      }
      fetchInitialOptions();
      pushUsersToFuse(diffBusinessUsers);
    }
    fetchDiffUsers();
  }, [apt.apt_fields, fetchUsers, fieldId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedSearchFunction = useCallback(
    debounce(async (query) => {
      const response = businessUsersFuse.search(query);
      const filteredOptions = response?.map(({ item }) => item) || [];
      setFilteredOptions(
        filteredOptions.map(convertBusinessUserToAptFieldOption)
      );
    }),
    []
  );

  useEffect(() => {
    debouncedSearchFunction(searchQuery);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const searchOptions = useMemo(() => {
    if (searchQuery && searchQuery.length > 0) {
      return filteredOptions;
    }
    return fieldOptions || [];
  }, [fieldOptions, filteredOptions, searchQuery]);

  const handleOptionSelect = (optionsToPush: TAptFieldOption[]) => {
    pushAptFieldToApt({
      appointment: apt,
      fieldId: fieldId,
      fieldValues: optionsToPush.map(({ id, value, description }) => id),
    });
  };
  return (
    <SelectionDropdown
      searchQuery={searchQuery}
      onSearch={setSearchQuery}
      dropdownOptions={fieldOptions}
      filteredOptions={searchOptions}
      type={subType || "multi"}
      title={title || ""}
      onOptionSelect={handleOptionSelect}
      onDropdownAction={setFetchUsers}
      isDisabled={isDisabled}
    />
  );
};

export default PersonSelectDropdown;
